import React from 'react'
import { Space } from 'antd'
import DatePicker from '../../DatePicker.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import Button from '../../Button.jsx'
import type { DateRangeFilterValue, FilterConfig, FilterLocalState, FilterSelectedKey } from '../TableViewTypes'
import { dateRangeValueFromSelectedKey } from './filterSelectedKey.ts'

interface DateRangeFilterDropdownProps {
  columnKey: string
  actualFilterKey: string
  filterLocalState: FilterLocalState
  setFilterLocalState: React.Dispatch<React.SetStateAction<FilterLocalState>>
  selectedKeys: FilterSelectedKey[]
  setSelectedKeys: (selectedKeys: FilterSelectedKey[]) => void
  confirm: () => void
  onFiltersChange?: (filters: FilterConfig) => void
}

export default function DateRangeFilterDropdown({
  columnKey,
  actualFilterKey,
  filterLocalState,
  setFilterLocalState,
  selectedKeys,
  setSelectedKeys,
  confirm,
  onFiltersChange
}: DateRangeFilterDropdownProps) {
  const localValue = filterLocalState[columnKey]?.dateRange
  const dateRangeValue: DateRangeFilterValue =
    localValue !== undefined ? localValue : dateRangeValueFromSelectedKey(selectedKeys[0])
  const hasValue =
    (dateRangeValue.min !== undefined && dateRangeValue.min !== null && dateRangeValue.min !== '') ||
    (dateRangeValue.max !== undefined && dateRangeValue.max !== null && dateRangeValue.max !== '')

  if (localValue !== undefined && JSON.stringify(selectedKeys[0] || {}) !== JSON.stringify(localValue)) {
    setSelectedKeys([localValue])
  }

  const handleApply = () => {
    if (hasValue) {
      setFilterLocalState((prev) => {
        const newState = { ...prev }
        delete newState[columnKey]
        return newState
      })
      confirm()
    }
  }

  const handleClear = () => {
    setFilterLocalState((prev) => {
      const newState = { ...prev }
      delete newState[columnKey]
      return newState
    })

    setSelectedKeys([])

    const filterConfig: FilterConfig = {}
    filterConfig[actualFilterKey] = undefined
    onFiltersChange?.(filterConfig)

    confirm()
  }

  return (
    <div style={{ padding: 8, width: 400 }}>
      <div style={{ marginBottom: 8 }}>
        <DatePicker
          placeholder='From Date'
          value={dateRangeValue.min ? dayjs(dateRangeValue.min) : null}
          autoFocus
          onChange={(date: dayjs.Dayjs | null) => {
            const newRange = { ...dateRangeValue }
            if (date === null || date === undefined) {
              delete newRange.min
            } else {
              newRange.min = date.format('YYYY-MM-DD')
            }
            setFilterLocalState((prev) => ({
              ...prev,
              [columnKey]: { ...prev[columnKey], dateRange: newRange }
            }))
            setSelectedKeys([newRange])
          }}
          style={{ width: '50%', marginBottom: 8 }}
          format='YYYY-MM-DD'
        />
        <DatePicker
          placeholder='To Date'
          value={dateRangeValue.max ? dayjs(dateRangeValue.max) : null}
          onChange={(date: dayjs.Dayjs | null) => {
            const newRange = { ...dateRangeValue }
            if (date === null || date === undefined) {
              delete newRange.max
            } else {
              newRange.max = date.format('YYYY-MM-DD')
            }
            setFilterLocalState((prev) => ({
              ...prev,
              [columnKey]: { ...prev[columnKey], dateRange: newRange }
            }))
            setSelectedKeys([newRange])
          }}
          style={{ width: '48%', marginLeft: '2%' }}
          format='YYYY-MM-DD'
        />
      </div>
      <Space>
        <Button variant='primary' onClick={handleApply} size='small' disabled={!hasValue}>
          Apply
        </Button>
        <Button variant='cancel' onClick={handleClear} size='small'>
          Clear
        </Button>
      </Space>
    </div>
  )
}

export function DateRangeFilterIcon(filtered: boolean) {
  return <FontAwesomeIcon icon={faFilter} style={{ color: filtered ? '#1890ff' : '#9CA3AF', padding: '0 4px' }} />
}
